import { useEffect, useState, useCallback } from 'react'
import { FileText, CheckCircle2, Hourglass, XCircle, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, AlertTriangle, TrendingUp, Receipt, ArrowRight, Hammer } from 'lucide-react'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import {
  getBusinessDashboard,
  getCaTimeline,
  getMonthlyKpis,
  getReworkRates,
  getSalesByMonth,
  type BusinessDashboardDto,
  type DashboardPeriod,
  type ProjectTypeFunnelDto,
  type PendingQuoteAgingBucketDto,
  type CaTimelineResponseDto,
  type MonthlyKpisDto,
  type ReworkRatesDto,
  type SalesByMonthDto,
  type SalesMonthCellDto,
} from '@/lib/api/v2/analytics'
import { getActivityFeed } from '@/lib/api/v2/activity'
import { ActivityFeed } from '@/components/features/activity/ActivityFeed'
import type { ActivityItemDto } from '@/types'

// ── Period selector ────────────────────────────────────────────────────────────

const PERIOD_OPTIONS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: 'month', label: 'Ce mois' },
  { value: 'quarter', label: 'Ce trimestre' },
  { value: 'year', label: 'Cette année' },
]

// ── Formatters ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return amount.toLocaleString('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  })
}

function formatPercent(value: number | null): string {
  if (value === null) return '—'
  return `${Math.round(value)}%`
}

function formatDays(days: number | null): string {
  if (days === null) return '—'
  return `${Math.round(days)}j`
}

/** Compact currency label for chart bars, e.g. 1 250 € → "1.3k€" */
function formatCompact(amount: number): string {
  if (amount === 0) return ''
  if (amount >= 1000) return `${(amount / 1000).toLocaleString('fr-BE', { maximumFractionDigits: 1 })}k€`
  return `${Math.round(amount)}€`
}

// ── Delta badge (DASH-005) ─────────────────────────────────────────────────────

type DeltaFormat = 'pct' | 'pts' | 'count'

interface DeltaBadgeProps {
  current: number | null
  previous: number | null
  format: DeltaFormat
  label: string   // e.g. "vs Mar 2025"
}

function DeltaBadge({ current, previous, format, label }: DeltaBadgeProps) {
  if (current === null || previous === null) return null

  let delta: number
  let formatted: string

  if (format === 'pct') {
    // % revenue change: (current - prev) / prev * 100
    delta = previous === 0 ? 0 : Math.round(((current - previous) / previous) * 100)
    formatted = `${delta >= 0 ? '+' : ''}${delta}%`
  } else if (format === 'pts') {
    // Percentage point change
    delta = Math.round((current - previous) * 10) / 10
    formatted = `${delta >= 0 ? '+' : ''}${delta}pts`
  } else {
    // Absolute count change
    delta = Math.round(current - previous)
    formatted = `${delta >= 0 ? '+' : ''}${delta}`
  }

  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '→'
  const colorClass = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-muted-foreground'

  return (
    <span className={cn('text-xs font-medium tabular-nums', colorClass)}>
      {arrow} {formatted} <span className="text-muted-foreground font-normal">{label}</span>
    </span>
  )
}

// ── Rework rate cards (SOLY-FINITION-001 MS-04) ───────────────────────────────

/**
 * Trend badge for rates where a DECREASE is the good news (rework): green when falling, red when
 * rising — the inverse of DeltaBadge's growth-is-good colouring, hence a dedicated component
 * instead of reusing it.
 */
function ImprovementDeltaBadge({ current, previous, label }: { current: number; previous: number; label: string }) {
  // Rates are 0..1 → percentage-point delta, one decimal (mirrors DeltaBadge's 'pts' format).
  const delta = Math.round((current - previous) * 1000) / 10
  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '→'
  const colorClass = delta < 0 ? 'text-emerald-600' : delta > 0 ? 'text-red-500' : 'text-muted-foreground'
  return (
    <span className={cn('text-xs font-medium tabular-nums', colorClass)}>
      {arrow} {delta >= 0 ? '+' : ''}{delta}pts <span className="text-muted-foreground font-normal">{label}</span>
    </span>
  )
}

/**
 * The rework KPI: « Chantiers non finis du 1er coup » — the lever Thierry wants to drive down.
 * Counts and the 0..1 rate come straight from /v2/analytics/rework-rates; 0 projects renders as
 * « 0% ». SOLY-PLAN-003 removed the secondary « Taux de SAV » card with the nature behind it; the
 * grid keeps its 3 columns so the card holds the same width as before, aligned with the KPI row.
 */
export function ReworkRateCards({ data }: { data: ReworkRatesDto }) {
  const { current, previous } = data
  const plural = (n: number) => (n > 1 ? 's' : '')
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card data-testid="analytics-finition-rate-card" className="py-4 gap-3">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>Chantiers non finis du 1er coup</CardDescription>
            <div className="text-muted-foreground"><Hammer className="h-5 w-5" /></div>
          </div>
          <CardTitle className="text-2xl font-bold tabular-nums">{formatPercent(current.finitionRate * 100)}</CardTitle>
          <p className="text-sm text-muted-foreground -mt-1">
            {current.finitionProjectsCount} chantier{plural(current.finitionProjectsCount)} avec finition · {current.totalProjectsInExecution} en exécution
          </p>
        </CardHeader>
        <CardContent>
          <ImprovementDeltaBadge current={current.finitionRate} previous={previous.finitionRate} label="vs période précédente" />
        </CardContent>
      </Card>
    </div>
  )
}

// ── Pending quotes aging card (DASH-004) ──────────────────────────────────────


interface PendingQuotesAgingCardProps {
  buckets: PendingQuoteAgingBucketDto[]
  total: { count: number; amount: number }
  'data-testid'?: string
}

function PendingQuotesAgingCard({ buckets, total, 'data-testid': testId }: PendingQuotesAgingCardProps) {
  const fresh    = buckets.find(b => b.bucket === 'fresh')
  const warming  = buckets.find(b => b.bucket === 'warming')
  const critical = buckets.find(b => b.bucket === 'critical')

  return (
    <Card data-testid={testId} className="py-4 gap-3">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardDescription>Devis en attente</CardDescription>
          <div className="text-muted-foreground"><FileText className="h-5 w-5" /></div>
        </div>
        <CardTitle className="text-2xl font-bold tabular-nums">
          {fresh && fresh.count > 0 ? formatCurrency(fresh.amount) : '—'}
        </CardTitle>
        <p className="text-sm text-muted-foreground -mt-1">
          {fresh && fresh.count > 0
            ? `${fresh.count} à convertir (<15j)`
            : 'Aucun devis récent'}
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5 border-t pt-3">
        {warming && warming.count > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-amber-500">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
              {warming.count} devis 15–30j
            </span>
            <span className="tabular-nums text-muted-foreground">{formatCurrency(warming.amount)}</span>
          </div>
        )}
        {critical && critical.count > 0 && (
          <div className="flex items-center justify-between text-xs text-red-400">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
              {critical.count} &gt;30j
            </span>
            <span className="tabular-nums">{formatCurrency(critical.amount)}</span>
          </div>
        )}
        {total.count === 0 && (
          <p className="text-sm text-muted-foreground">Aucun devis en attente</p>
        )}
      </CardContent>
    </Card>
  )
}

// ── Monthly KPI header card (DASH-007) ───────────────────────────────────────

interface MonthlyKpiHeaderCardProps {
  label: string
  value: string
  currentAmount: number
  prevAmount: number | null
  comparisonLabel: string | null
  targetDeltaPct: number | null
  icon: React.ReactNode
  'data-testid'?: string
}

function targetColorClass(pct: number | null): string {
  if (pct === null) return ''
  if (pct >= 100) return 'text-emerald-600'
  if (pct >= 75) return 'text-amber-500'
  return 'text-red-500'
}

function MonthlyKpiHeaderCard({ label, value, currentAmount, prevAmount, comparisonLabel, targetDeltaPct, icon, 'data-testid': testId }: MonthlyKpiHeaderCardProps) {
  return (
    <Card data-testid={testId} className="py-4 gap-3">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardDescription>{label}</CardDescription>
          <div className="text-muted-foreground">{icon}</div>
        </div>
        <CardTitle className="text-2xl font-bold tabular-nums">{value}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {targetDeltaPct !== null && (
          <p className={cn('text-sm font-medium tabular-nums', targetColorClass(targetDeltaPct))}>
            {Math.round(targetDeltaPct)}% de l'objectif mensuel
          </p>
        )}
        {prevAmount !== null && comparisonLabel && (
          <DeltaBadge
            current={currentAmount}
            previous={prevAmount}
            format="pct"
            label={comparisonLabel}
          />
        )}
      </CardContent>
    </Card>
  )
}

// ── CA Timeline chart (DASH-006) ──────────────────────────────────────────────

const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

interface CaTimelineChartProps {
  data: CaTimelineResponseDto
  onNavigate: (offset: number) => void
}

function CaTimelineChart({ data, onNavigate }: CaTimelineChartProps) {
  const { months, unplannedAmount, monthlyRevenueTarget, windowOffset } = data

  const maxBar = Math.max(
    ...months.map((m) => m.invoiced + m.scheduled),
    monthlyRevenueTarget ?? 0,
    1, // avoid divide-by-zero
  )

  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1

  return (
    <Card className="py-4 gap-3">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardDescription>CA mensuel</CardDescription>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onNavigate(windowOffset - 1)}
              className="p-1 rounded hover:bg-muted transition-colors"
              aria-label="Mois précédents"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <button
              type="button"
              onClick={() => onNavigate(0)}
              className="px-2 py-0.5 text-xs rounded hover:bg-muted transition-colors text-muted-foreground"
              aria-label="Revenir au mois courant"
            >
              Aujourd'hui
            </button>
            <button
              type="button"
              onClick={() => onNavigate(windowOffset + 1)}
              className="p-1 rounded hover:bg-muted transition-colors"
              aria-label="Mois suivants"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
        <CardTitle>Réalisé + Prévisionnel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm bg-primary" />
            Facturé
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm bg-primary/30" />
            Planifié
          </span>
          {monthlyRevenueTarget !== null && (
            <span className="flex items-center gap-1.5">
              <span className="h-0.5 w-6 border-t-2 border-dashed border-amber-500" />
              Objectif
            </span>
          )}
        </div>

        {/* Chart */}
        <div className="relative">
          {/* Target line (SVG overlay) */}
          {monthlyRevenueTarget !== null && maxBar > 0 && (
            <div
              className="absolute left-0 right-0 border-t-2 border-dashed border-amber-500 pointer-events-none z-10"
              style={{ bottom: `${(monthlyRevenueTarget / maxBar) * 160}px` }}
            />
          )}

          <div className="flex items-end gap-1" style={{ height: 160 }}>
            {months.map((m) => {
              const total = m.invoiced + m.scheduled
              const invoicedH = maxBar > 0 ? (m.invoiced / maxBar) * 160 : 0
              const scheduledH = maxBar > 0 ? (m.scheduled / maxBar) * 160 : 0
              const isCurrentMonth = m.year === currentYear && m.month === currentMonth
              const label = `${MONTH_LABELS[m.month - 1]} ${m.year !== currentYear ? m.year : ''}`

              return (
                <div key={`${m.year}-${m.month}`} className="group flex-1 flex flex-col items-center gap-1 relative">
                  {/* Hover tooltip with invoiced/scheduled breakdown */}
                  {total > 0 && (
                    <div className="absolute bottom-full mb-1 z-20 hidden group-hover:flex flex-col items-start gap-0.5 bg-popover border rounded-md shadow-md px-2.5 py-2 text-xs whitespace-nowrap pointer-events-none left-1/2 -translate-x-1/2">
                      {m.invoiced > 0 && (
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-sm bg-primary shrink-0" />
                          <span className="text-muted-foreground">Facturé</span>
                          <span className="font-medium tabular-nums">{formatCurrency(m.invoiced)}</span>
                        </span>
                      )}
                      {m.scheduled > 0 && (
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-sm bg-primary/30 shrink-0" />
                          <span className="text-muted-foreground">Planifié</span>
                          <span className="font-medium tabular-nums">{formatCurrency(m.scheduled)}</span>
                        </span>
                      )}
                      {m.invoiced > 0 && m.scheduled > 0 && (
                        <span className="flex items-center gap-1.5 border-t pt-0.5 mt-0.5 w-full">
                          <span className="h-2 w-2 shrink-0" />
                          <span className="text-muted-foreground">Total</span>
                          <span className="font-semibold tabular-nums">{formatCurrency(total)}</span>
                        </span>
                      )}
                    </div>
                  )}

                  <div className="w-full flex flex-col justify-end" style={{ height: 140 }}>
                    {total === 0 ? (
                      <div className="w-full h-0.5 bg-border rounded" />
                    ) : (
                      <div className="w-full flex flex-col justify-end relative">
                        {/* Amount label above the bar */}
                        <span className="absolute -top-5 left-0 right-0 text-center text-[9px] font-medium text-foreground/70 tabular-nums leading-none">
                          {formatCompact(total)}
                        </span>
                        {scheduledH > 0 && (
                          <div
                            className="w-full bg-primary/30 rounded-t-sm"
                            style={{ height: Math.max(scheduledH, 2) }}
                          />
                        )}
                        {invoicedH > 0 && (
                          <div
                            className={cn('w-full bg-primary', scheduledH === 0 && 'rounded-t-sm')}
                            style={{ height: Math.max(invoicedH, 2) }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-[10px] whitespace-nowrap text-muted-foreground text-center',
                      isCurrentMonth && 'text-primary font-semibold',
                    )}
                  >
                    {label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Monthly amounts table */}
        {months.some((m) => m.invoiced > 0 || m.scheduled > 0) && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground">
                  <td className="py-0.5 pr-3 font-medium w-20" />
                  {months.map((m) => {
                    const isCurrentMonth = m.year === currentYear && m.month === currentMonth
                    return (
                      <td
                        key={`${m.year}-${m.month}`}
                        className={cn(
                          'py-0.5 px-1 text-center tabular-nums',
                          isCurrentMonth && 'text-primary font-semibold',
                        )}
                      >
                        {MONTH_LABELS[m.month - 1]}{m.year !== currentYear ? ` ${m.year}` : ''}
                      </td>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-0.5 pr-3 text-muted-foreground font-medium flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-primary inline-block shrink-0" />
                    Facturé
                  </td>
                  {months.map((m) => (
                    <td key={`inv-${m.year}-${m.month}`} className="py-0.5 px-1 text-center tabular-nums text-foreground/80">
                      {m.invoiced > 0 ? formatCompact(m.invoiced) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-0.5 pr-3 text-muted-foreground font-medium flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-primary/30 inline-block shrink-0" />
                    Planifié
                  </td>
                  {months.map((m) => (
                    <td key={`sched-${m.year}-${m.month}`} className="py-0.5 px-1 text-center tabular-nums text-foreground/80">
                      {m.scheduled > 0 ? formatCompact(m.scheduled) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Unplanned callout */}
        {unplannedAmount > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{formatCurrency(unplannedAmount)}</strong> signés — date de planification manquante
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Sales by month table + drill-down (DASH-008) ──────────────────────────────

const MONTH_LABELS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

interface DrillDownPanelProps {
  cell: SalesMonthCellDto
  salesPersonName: string
  monthLabel: string
  onClose: () => void
}

function DrillDownPanel({ cell, salesPersonName, monthLabel, onClose }: DrillDownPanelProps) {
  const navigate = useNavigate()

  return (
    <div className="mt-2 rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          {salesPersonName} — {monthLabel}
          <span className="ml-2 text-muted-foreground tabular-nums">
            {formatCurrency(cell.amount)}
          </span>
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Fermer
        </button>
      </div>

      {cell.projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun projet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs">
                <th className="pb-1.5 text-left font-medium">Client</th>
                <th className="pb-1.5 text-left font-medium">Type</th>
                <th className="pb-1.5 text-right font-medium">Montant signé</th>
                <th className="pb-1.5 text-right font-medium">Date de pose</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cell.projects.map((p) => {
                const planningDate = p.planningDate
                  ? new Date(p.planningDate).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : null
                return (
                  <tr
                    key={p.projectId}
                    className="hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/projects/${p.projectId}`)}
                  >
                    <td className="py-2 pr-4 font-medium">{p.clientName || '—'}</td>
                    <td className="py-2 pr-4 text-muted-foreground capitalize">{p.typeLabel || '—'}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{formatCurrency(p.amount)}</td>
                    <td className="py-2 text-right tabular-nums">
                      {planningDate ? (
                        <span>{planningDate}</span>
                      ) : (
                        <span className="text-amber-500 font-medium">Non planifié</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface SalesByMonthTableProps {
  data: SalesByMonthDto
}

function SalesByMonthTable({ data }: SalesByMonthTableProps) {
  const [activeCell, setActiveCell] = useState<{ rowIdx: number; colIdx: number } | null>(null)

  if (data.months.length === 0 || data.rows.length === 0) return null

  const handleCellClick = (rowIdx: number, colIdx: number) => {
    const cell = data.rows[rowIdx].cells[colIdx]
    if (cell.amount === 0) return // empty cell — no drill-down
    setActiveCell((prev) =>
      prev?.rowIdx === rowIdx && prev?.colIdx === colIdx ? null : { rowIdx, colIdx }
    )
  }

  return (
    <Card className="py-4 gap-3">
      <CardHeader>
        <CardDescription>Ventes 6 derniers mois</CardDescription>
        <CardTitle>Performances commerciales</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-2 text-left font-medium text-muted-foreground w-32 shrink-0">Vendeur</th>
                {data.months.map((m) => (
                  <th key={`${m.year}-${m.month}`} className="pb-2 text-right font-medium text-muted-foreground whitespace-nowrap px-2">
                    {MONTH_LABELS_SHORT[m.month - 1]} {m.year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map((row, rowIdx) => (
                <tr
                  key={row.salesPersonId ?? (row.isTotal ? 'total' : 'unassigned')}
                  className={row.isTotal ? 'font-semibold' : ''}
                >
                  <td className="py-2.5 pr-4 whitespace-nowrap">
                    {row.salesPersonName}
                  </td>
                  {row.cells.map((cell, colIdx) => {
                    const isActive = activeCell?.rowIdx === rowIdx && activeCell?.colIdx === colIdx
                    return (
                      <td key={colIdx} className="py-2.5 px-2 text-right tabular-nums">
                        {cell.amount === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleCellClick(rowIdx, colIdx)}
                            className={cn(
                              'tabular-nums rounded px-1 transition-colors',
                              isActive
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted'
                            )}
                          >
                            {formatCurrency(cell.amount)}
                          </button>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Drill-down panel */}
        {activeCell !== null && (() => {
          const row = data.rows[activeCell.rowIdx]
          const cell = row.cells[activeCell.colIdx]
          const m = data.months[activeCell.colIdx]
          const monthLabel = `${MONTH_LABELS_SHORT[m.month - 1]} ${m.year}`
          return (
            <DrillDownPanel
              cell={cell}
              salesPersonName={row.salesPersonName}
              monthLabel={monthLabel}
              onClose={() => setActiveCell(null)}
            />
          )
        })()}
      </CardContent>
    </Card>
  )
}


// ── Funnel outcome block ───────────────────────────────────────────────────────

interface OutcomeBlockProps {
  won: number
  lost: number
  pending: number
}

function OutcomeBlock({ won, lost, pending }: OutcomeBlockProps) {
  const total = won + lost + pending
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0)
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border bg-card px-4 py-3 shadow-sm min-w-[160px]">
      {total === 0 ? (
        <span className="text-sm text-muted-foreground">Aucun résultat</span>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {won} gagné{won !== 1 ? 's' : ''}
            </span>
            <div className="h-1 w-full rounded-full bg-muted">
              <div className="h-1 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct(won)}%` }} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-red-500">
              <XCircle className="h-4 w-4 shrink-0" />
              {lost} perdu{lost !== 1 ? 's' : ''}
            </span>
            <div className="h-1 w-full rounded-full bg-muted">
              <div className="h-1 rounded-full bg-red-400 transition-all duration-500" style={{ width: `${pct(lost)}%` }} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-amber-500">
              <Hourglass className="h-4 w-4 shrink-0" />
              {pending} sans réponse
            </span>
            <div className="h-1 w-full rounded-full bg-muted">
              <div className="h-1 rounded-full bg-amber-400 transition-all duration-500" style={{ width: `${pct(pending)}%` }} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── Funnel stage ───────────────────────────────────────────────────────────────

interface FunnelStageProps {
  label: string
  count: number
  fillPct: number
  convRate?: number | null
  isFirst?: boolean
  'data-testid'?: string
}

function FunnelStage({ label, count, fillPct, convRate, isFirst = false, 'data-testid': testId }: FunnelStageProps) {
  return (
    <div className="flex items-center gap-2">
      {!isFirst && (
        <div className="flex flex-col items-center gap-0.5 shrink-0">
          <span className="text-muted-foreground">→</span>
          {convRate !== undefined && convRate !== null && (
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              {formatPercent(convRate)}
            </span>
          )}
        </div>
      )}
      <div
        data-testid={testId}
        className="flex flex-col items-center rounded-xl border bg-card px-4 py-3 shadow-sm min-w-[100px]"
      >
        <span className="text-2xl font-bold tabular-nums">{count}</span>
        <span className="text-xs text-muted-foreground mt-0.5 text-center">{label}</span>
        <div className="w-full h-1 rounded-full bg-muted mt-2">
          <div
            className="h-1 rounded-full bg-primary transition-all duration-500"
            style={{ width: `${fillPct}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// ── Performance by project type table ─────────────────────────────────────────

type SortColumn =
  | 'typeLabel'
  | 'projectsWithQuoteSent'
  | 'projectsWon'
  | 'projectsPending'
  | 'conversionRateGlobal'
  | 'revenueWon'
  | 'revenueShare'
  | 'avgDealSize'
  | 'avgDaysToClose'

type SortDirection = 'asc' | 'desc'

function convRateColor(rate: number | null): string {
  if (rate === null) return ''
  if (rate >= 80) return 'text-emerald-600 font-medium'
  if (rate >= 50) return 'text-amber-500 font-medium'
  return 'text-red-500 font-medium'
}

interface SortableHeaderProps {
  label: string
  column: SortColumn
  activeColumn: SortColumn
  direction: SortDirection
  onSort: (col: SortColumn) => void
  align?: 'left' | 'right'
}

function SortableHeader({ label, column, activeColumn, direction, onSort, align = 'right' }: SortableHeaderProps) {
  const isActive = column === activeColumn
  const Icon = isActive ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown
  return (
    <th
      className={cn(
        'pb-2 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap',
        'hover:text-foreground transition-colors',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      onClick={() => onSort(column)}
    >
      <span className={cn('inline-flex items-center gap-1', align === 'right' && 'flex-row-reverse')}>
        {label}
        <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-foreground' : 'text-muted-foreground/50')} />
      </span>
    </th>
  )
}

interface TypeBreakdownTableProps {
  rows: ProjectTypeFunnelDto[]
  totals: {
    projectsWithQuoteSent: number
    projectsWon: number
    projectsPending: number
    conversionRateGlobal: number | null
    revenueWon: number
    avgDealSize: number | null
  }
}

function TypeBreakdownTable({ rows, totals }: TypeBreakdownTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('revenueWon')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  if (rows.length === 0) return null

  const handleSort = (col: SortColumn) => {
    if (col === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(col)
      setSortDirection('desc')
    }
  }

  const sortedRows = [...rows].sort((a, b) => {
    // "Autre" always last regardless of sort
    if (a.typeCode === 'autre' && b.typeCode !== 'autre') return 1
    if (b.typeCode === 'autre' && a.typeCode !== 'autre') return -1

    const aVal = a[sortColumn] ?? (sortDirection === 'asc' ? Infinity : -Infinity)
    const bVal = b[sortColumn] ?? (sortDirection === 'asc' ? Infinity : -Infinity)
    const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal as string) : (aVal as number) - (bVal as number)
    return sortDirection === 'asc' ? cmp : -cmp
  })

  const headerProps = { activeColumn: sortColumn, direction: sortDirection, onSort: handleSort }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <SortableHeader label="Type" column="typeLabel" {...headerProps} align="left" />
            <SortableHeader label="Devis envoyés" column="projectsWithQuoteSent" {...headerProps} />
            <SortableHeader label="Gagnés" column="projectsWon" {...headerProps} />
            <SortableHeader label="Sans réponse" column="projectsPending" {...headerProps} />
            <SortableHeader label="Taux conv." column="conversionRateGlobal" {...headerProps} />
            <SortableHeader label="CA gagné" column="revenueWon" {...headerProps} />
            <SortableHeader label="% CA" column="revenueShare" {...headerProps} />
            <SortableHeader label="Cmd. moyenne" column="avgDealSize" {...headerProps} />
            <SortableHeader label="Délai moy." column="avgDaysToClose" {...headerProps} />
          </tr>
        </thead>
        <tbody className="divide-y">
          {sortedRows.map((row) => (
            <tr key={row.typeCode} className="hover:bg-muted/30 transition-colors">
              <td className="py-2.5 font-medium capitalize">{row.typeLabel}</td>
              <td className="py-2.5 text-right tabular-nums">{row.projectsWithQuoteSent}</td>
              <td className="py-2.5 text-right tabular-nums text-emerald-600 font-medium">{row.projectsWon}</td>
              <td className="py-2.5 text-right tabular-nums text-amber-500">{row.projectsPending}</td>
              <td className={cn('py-2.5 text-right tabular-nums', convRateColor(row.conversionRateGlobal))}>
                {formatPercent(row.conversionRateGlobal)}
              </td>
              <td className="py-2.5 text-right tabular-nums">{formatCurrency(row.revenueWon)}</td>
              <td className="py-2.5 text-right tabular-nums text-muted-foreground">
                {row.revenueShare !== null ? `${row.revenueShare}%` : '—'}
              </td>
              <td className="py-2.5 text-right tabular-nums">
                {row.avgDealSize !== null ? formatCurrency(row.avgDealSize) : '—'}
              </td>
              <td className="py-2.5 text-right tabular-nums text-muted-foreground">
                {formatDays(row.avgDaysToClose)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t font-semibold">
            <td className="pt-2.5">Total</td>
            <td className="pt-2.5 text-right tabular-nums">{totals.projectsWithQuoteSent}</td>
            <td className="pt-2.5 text-right tabular-nums text-emerald-600">{totals.projectsWon}</td>
            <td className="pt-2.5 text-right tabular-nums text-amber-500">{totals.projectsPending}</td>
            <td className={cn('pt-2.5 text-right tabular-nums', convRateColor(totals.conversionRateGlobal))}>
              {formatPercent(totals.conversionRateGlobal)}
            </td>
            <td className="pt-2.5 text-right tabular-nums">{formatCurrency(totals.revenueWon)}</td>
            <td className="pt-2.5 text-right tabular-nums text-muted-foreground">100%</td>
            <td className="pt-2.5 text-right tabular-nums">
              {totals.avgDealSize !== null ? formatCurrency(totals.avgDealSize) : '—'}
            </td>
            <td className="pt-2.5" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ── Skeleton ───────────────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="py-4 gap-3">
            <CardHeader>
              <div className="h-4 w-24 rounded bg-muted animate-pulse" />
              <div className="h-7 w-32 rounded bg-muted animate-pulse mt-2" />
            </CardHeader>
            <CardContent>
              <div className="h-3 w-20 rounded bg-muted animate-pulse" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="py-4 gap-3">
        <CardHeader>
          <div className="h-4 w-40 rounded bg-muted animate-pulse" />
          <div className="h-6 w-64 rounded bg-muted animate-pulse mt-2" />
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 w-28 rounded bg-muted animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function BusinessDashboardPage() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<DashboardPeriod>('month')
  const [data, setData] = useState<BusinessDashboardDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // CA Timeline — independent from the global period filter
  const [timelineData, setTimelineData] = useState<CaTimelineResponseDto | null>(null)

  // Monthly KPIs — independent from the global period filter (DASH-007)
  const [monthlyKpis, setMonthlyKpis] = useState<MonthlyKpisDto | null>(null)

  useEffect(() => {
    getMonthlyKpis()
      .then(setMonthlyKpis)
      .catch(() => {/* non-blocking */})
  }, [])

  // Rework KPIs (SOLY-FINITION-001 MS-04) — follows the SAME period filter as the dashboard.
  const [reworkRates, setReworkRates] = useState<ReworkRatesDto | null>(null)

  useEffect(() => {
    getReworkRates(period)
      .then(setReworkRates)
      .catch(() => {/* non-blocking */})
  }, [period])

  // Sales by month — fixed 6-month rolling window, independent from global period filter (DASH-008)
  const [salesByMonth, setSalesByMonth] = useState<SalesByMonthDto | null>(null)

  useEffect(() => {
    getSalesByMonth('6months')
      .then(setSalesByMonth)
      .catch(() => {/* non-blocking */})
  }, [])

  // Activity Feed widget — 7 days, last 5 items
  const [activityItems, setActivityItems] = useState<ActivityItemDto[]>([])
  const [activityLoading, setActivityLoading] = useState(true)

  useEffect(() => {
    getActivityFeed({ days: 7 })
      .then((res) => setActivityItems(res.items.slice(0, 5)))
      .catch(() => setActivityItems([]))
      .finally(() => setActivityLoading(false))
  }, [])

  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const result = await getBusinessDashboard(period)
        setData(result)
      } catch {
        setError('Impossible de charger les données analytiques.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchDashboard()
  }, [period])

  const fetchTimeline = useCallback((offset: number) => {
    getCaTimeline(offset)
      .then(setTimelineData)
      .catch(() => {/* non-blocking — chart stays hidden on error */})
  }, [])

  useEffect(() => {
    fetchTimeline(0)
  }, [fetchTimeline])

  const sentRate =
    data && data.projectsCreated > 0
      ? (data.projectsWithQuoteSent / data.projectsCreated) * 100
      : null

  const hasResolvedProjects = data ? data.projectsWon + data.projectsLost > 0 : false
  const hasTypeBreakdown = data ? data.funnelByType.length > 0 : false

  // Total revenueWon for the type table totals row
  const totalRevenueWon = data?.funnelByType.reduce((sum, r) => sum + r.revenueWon, 0) ?? 0

  return (
    <DashboardLayout>
      {/* ── Header ── */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Tableau de bord</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Vue d'ensemble de votre activité commerciale
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {isLoading ? (
        <DashboardSkeleton />
      ) : data ? (
        <div className="space-y-6">

          {/* ── Monthly KPI header (DASH-007) — independent from period filter ── */}
          {monthlyKpis && (
            <div className="grid gap-4 sm:grid-cols-3">
              <MonthlyKpiHeaderCard
                data-testid="analytics-monthly-sales"
                label="Ventes signées — mois courant"
                value={formatCurrency(monthlyKpis.ventesSignees)}
                currentAmount={monthlyKpis.ventesSignees}
                prevAmount={monthlyKpis.ventesSigneesPrev}
                comparisonLabel={monthlyKpis.comparisonLabel}
                targetDeltaPct={monthlyKpis.ventesSigneesDeltaPct}
                icon={<TrendingUp className="h-5 w-5" />}
              />
              <MonthlyKpiHeaderCard
                data-testid="analytics-monthly-revenue"
                label="CA facturé — mois courant"
                value={formatCurrency(monthlyKpis.caFacture)}
                currentAmount={monthlyKpis.caFacture}
                prevAmount={monthlyKpis.caFacturePrev}
                comparisonLabel={monthlyKpis.comparisonLabel}
                targetDeltaPct={monthlyKpis.caFactureDeltaPct}
                icon={<Receipt className="h-5 w-5" />}
              />
              <PendingQuotesAgingCard
                data-testid="analytics-pending-amount"
                buckets={data.pendingQuotesByAge}
                total={{ count: data.pendingQuotesCount, amount: data.pendingQuotesAmount }}
              />
            </div>
          )}

          {/* ── Rework KPIs (SOLY-FINITION-001 MS-04) — period-filtered like the analysis below ── */}
          {reworkRates && <ReworkRateCards data={reworkRates} />}

          {/* ── CA Timeline (DASH-006) — independent from period filter ── */}
          {timelineData && (
            <CaTimelineChart
              data={timelineData}
              onNavigate={fetchTimeline}
            />
          )}

          {/* ── Sales by month (DASH-008) ── */}
          {salesByMonth && salesByMonth.months.length > 0 && (
            <SalesByMonthTable data={salesByMonth} />
          )}

          {/* ── Section: Analyse de l'activité (period-filtered) ── */}
          <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
            {/* Section header with period selector */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground">Analyse de l'activité</p>
                <p className="text-xs text-muted-foreground">Filtré par période</p>
              </div>
              <div className="flex gap-1.5">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`analytics-period-${opt.value}`}
                    onClick={() => setPeriod(opt.value)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      period === opt.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:border-primary text-muted-foreground',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

          {/* ── Row — Funnel (2/3) + Closing rate (1/3) ── */}
          <div className="grid gap-3 sm:grid-cols-3">
            {/* Funnel — 2/3 */}
            <div className="bg-background rounded-lg p-4 space-y-4 sm:col-span-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Funnel de conversion
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <FunnelStage
                  data-testid="analytics-funnel-projects"
                  label="Créés"
                  count={data.projectsCreated}
                  fillPct={100}
                  isFirst
                />
                <FunnelStage
                  data-testid="analytics-funnel-quotes-issued"
                  label="Devis envoyés"
                  count={data.projectsWithQuoteSent}
                  fillPct={data.projectsCreated > 0 ? Math.round(data.projectsWithQuoteSent / data.projectsCreated * 100) : 0}
                  convRate={sentRate}
                />
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-0.5 shrink-0">
                    <span className="text-muted-foreground">→</span>
                    {data.conversionRateGlobal !== null && (
                      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                        {formatPercent(data.conversionRateGlobal)}
                      </span>
                    )}
                  </div>
                  <OutcomeBlock
                    won={data.projectsWon}
                    lost={data.projectsLost}
                    pending={data.projectsPending}
                  />
                </div>
              </div>
              {hasResolvedProjects && (
                <p className="text-sm text-muted-foreground border-t pt-3">
                  ⏱ Devis envoyé en{' '}
                  <span className="font-semibold text-foreground">
                    {formatDays(data.avgDaysCreatedToQuoteSent)}
                  </span>{' '}
                  en moy.
                  {' · '}
                  Conclu en{' '}
                  <span className="font-semibold text-foreground">
                    {formatDays(data.avgDaysQuoteSentToResolved)}
                  </span>{' '}
                  en moy.
                </p>
              )}
              {data.projectsCreated === 0 && (
                <p className="text-sm text-muted-foreground">Aucun projet créé sur cette période.</p>
              )}
            </div>

            {/* Taux de closing — 1/3 */}
            <div className="bg-background rounded-lg p-4 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Taux de closing
                </p>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold tabular-nums">
                {formatPercent(data.conversionRateResolved)}
              </p>
              <p className="text-sm text-muted-foreground">
                {data.projectsWon + data.projectsLost > 0
                  ? `${data.projectsWon} gagné${data.projectsWon !== 1 ? 's' : ''} · ${data.projectsLost} perdu${data.projectsLost !== 1 ? 's' : ''} · ${data.projectsPending} en attente`
                  : 'Aucun projet décidé sur cette période'}
              </p>
              {data.conversionRateGlobal !== null && (
                <div className="flex items-baseline gap-2 border-t pt-1.5 flex-wrap">
                  <span className="text-lg font-semibold tabular-nums">
                    {formatPercent(data.conversionRateGlobal)}
                  </span>
                  <span className="text-sm text-muted-foreground">taux de conversion</span>
                  {data.comparison?.conversionRateGlobal != null && data.comparison?.label && (
                    <DeltaBadge
                      current={data.conversionRateGlobal}
                      previous={data.comparison.conversionRateGlobal}
                      format="pts"
                      label={data.comparison.label}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Performance by project type ── */}
          {hasTypeBreakdown && (
            <div className="bg-background rounded-lg p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">
                Performance par type
              </p>
              <TypeBreakdownTable
                rows={data.funnelByType}
                totals={{
                  projectsWithQuoteSent: data.projectsWithQuoteSent,
                  projectsWon: data.projectsWon,
                  projectsPending: data.projectsPending,
                  conversionRateGlobal: data.conversionRateGlobal,
                  revenueWon: totalRevenueWon,
                  avgDealSize: data.avgDealSize,
                }}
              />
            </div>
          )}
          </div>{/* end analyse section */}
        </div>
      ) : null}

      {/* ── Activity Feed widget ── */}
      <div className="mt-8">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Activité récente</CardTitle>
              <button
                type="button"
                onClick={() => navigate('/activity')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Voir tout
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <CardDescription>7 derniers jours</CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityFeed
              items={activityItems}
              loading={activityLoading}
              emptyMessage="Aucune activité cette semaine"
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
